import { useRef, useState } from 'react';
import { api, apiError } from '../../../shared/api.js';
import { Icon } from '../../../layout/Icon.jsx';
import { useRfidScan } from '../../../shared/useRfidScan.js';
import { toast } from '../../../shared/useToast.js';
import { usePosState } from '../hooks/usePosState.js';

export function CardScanInPos() {
  const inputRef = useRef(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const setLink = usePosState((s) => s.setLink);

  async function resolve(uidRaw) {
    const uid = String(uidRaw || '').trim();
    if (!uid) return;
    setBusy(true);
    try {
      // Try active session by uid
      let session = null;
      try {
        const { data } = await api.get(`/sessions/by-uid/${encodeURIComponent(uid)}`);
        session = data?.session || data?.data || data;
      } catch (err) {
        if (err?.response?.status !== 404) throw err;
      }

      // Resolve card details (balance + status)
      let card = null;
      try {
        const { data } = await api.get(`/cards/by-uid/${encodeURIComponent(uid)}`);
        card = data?.card || data?.data || data;
      } catch (err) {
        if (err?.response?.status !== 404) throw err;
      }

      if (!card && !session) {
        toast.error('الكارت غير موجود');
        return;
      }

      // بطاقة فعّالة بلا جلسة: الشراء بالرصيد لا يشترط وجود صاحبها داخل القاعة.
      // يُربط الكارت وحده، فتظهر «رصيد الكارت» فقط (التعليق على الجلسة يحتاج جلسة).
      if (!session) {
        if (card.status === 'blocked' || card.status === 'lost') {
          toast.error(card.status === 'lost' ? 'الكارت مفقود — لا يمكن الدفع به' : 'الكارت محظور — لا يمكن الدفع به');
          return;
        }
        setLink({
          sessionId: null,
          cardUid: card.uid || uid,
          childId: null,
          childName: `كارت ${card.uid || uid}`,
          photoUrl: null,
          cardBalance: typeof card.balance === 'number' ? card.balance : 0,
          linkedBy: 'card'
        });
        toast.success(`تم ربط الكارت ${card.uid || uid} — الرصيد ${Number(card.balance || 0).toLocaleString('en-US')} د.ع`);
        setValue('');
        return;
      }

      const child = session.childId || session.child || {};
      const sessionCard = session.cardId || session.card || null;
      const balance =
        card?.balance ??
        (sessionCard && typeof sessionCard.balance === 'number' ? sessionCard.balance : null);
      setLink({
        sessionId: session._id || session.id,
        cardUid: card?.uid || sessionCard?.uid || uid,
        childId: child._id || child.id || null,
        childName: child.name || '—',
        photoUrl: child.photoUrl || null,
        cardBalance: balance,
        linkedBy: 'card'
      });
      toast.success(`تم ربط ${child.name || 'الجلسة'}`);
      setValue('');
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  }

  useRfidScan({
    inputRef,
    onScan: (uid) => resolve(uid),
    autoFocus: false,
    autoClear: true
  });

  function onKey(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      resolve(value);
    }
  }

  return (
    <div className="relative flex-1 min-w-[160px]">
      <input
        ref={inputRef}
        className="input pr-9 text-[12.5px]"
        value={value}
        placeholder="مسح كارت طفل"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKey}
        dir="ltr"
        style={{ textAlign: 'right', fontFamily: 'monospace' }}
        disabled={busy}
      />
      <Icon
        name="card"
        size={14}
        className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3 pointer-events-none"
      />
    </div>
  );
}
